// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type ListRenderItem,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { X } from 'lucide-react-native';
// Class-aware color scheme (data-theme/.dark driven) — NOT react-native's
// OS-level `useColorScheme`, which renders the wrong palette on reload
// (see repo CLAUDE.md). Mirrors the fix already applied across chat.
import { useColorScheme } from '../foundations/colorScheme';
import { createTheme } from '../design-system/theme';
import { ThreadEmptyState } from './ThreadEmptyState';
import { ThreadHeader } from './ThreadHeader';
import { ThreadParticipantStrip } from './ThreadParticipantStrip';
import { ChatRoomComposerRich, type ChatRoomComposerRichProps } from '../chat/ChatRoomComposer';
import { THREAD_TILE_MAX_WIDTH, TileMaxWidthContext } from '../chat/tileConstraint';
import { MessageRow } from '../chat/MessageRow';
import { useUiPreferencesStore } from '../preferences/uiPreferencesStore';
import { AttachmentTray, type StagedAttachment } from '../chat/composer/AttachmentTray';
import type { MessageMenuAction } from '../chat/MessageActionMenu';
import type { ReactionAggregate } from '../chat/ReactionsRow';
import type { ChatMessageVariant } from '../chat/ChatRoom.types';
import type { GameSession } from '../sync/useGameSessions';
import { deriveVariantFromBody, parseEnvelope } from '../appEvent';
import type { Message } from '../sync/persistence';
import { threadsStrings } from './strings';
import type { ThreadReplyStatus } from './types';
import type { ThreadReply, ThreadState, ThreadViewModelLike } from './viewModelContract';

/**
 * Attachment + rich-input handlers forwarded to the thread composer
 * (photos, docs, voice, GIFs, stickers, mentions, paste, poll/games/
 * location overrides). The host MUST wire these to THREAD-scoped send
 * paths (each handler posts a reply into the open thread, not the parent
 * conversation). `onSend`, `replyTo`, `onCancelReply`, and `disabled`
 * stay owned by the panel and cannot be overridden here.
 */
export type ThreadComposerProps = Omit<
  ChatRoomComposerRichProps,
  'onSend' | 'replyTo' | 'onCancelReply' | 'disabled'
>;

/** Per-reply message-action handlers (the Figma actions popover). */
export interface ThreadRowActions {
  /** Open the reaction picker for a reply. */
  readonly onReact?: ((reply: ThreadReply) => void) | undefined;
  /** Toggle a quick reaction emoji on a reply. */
  readonly onToggleReaction?: ((reply: ThreadReply, emoji: string) => void) | undefined;
  /** A secondary-menu action (copy/forward/pin/remove) on a reply. */
  readonly onMenuAction?:
    | ((action: MessageMenuAction, reply: ThreadReply) => void)
    | undefined;
  /**
   * Reflect a reply's pinned / saved / starred state in the action menu so
   * the entries read Unpin / Unsave / Unstar (and the toggle direction is
   * obvious). Host resolves the reply's content hash → membership; omitting
   * a predicate keeps the default "Pin" / "Save" / "Star" label.
   */
  readonly isReplyPinned?: ((reply: ThreadReply) => boolean) | undefined;
  readonly isReplySaved?: ((reply: ThreadReply) => boolean) | undefined;
  readonly isReplyStarred?: ((reply: ThreadReply) => boolean) | undefined;
  /** Aggregated reactions per reply id. */
  readonly reactionsByReplyId?:
    | Record<string, ReadonlyArray<ReactionAggregate>>
    | undefined;
  /**
   * Host-built variant for a reply. Lets the host inject the SAME live
   * context the main chat uses (resolved image object URLs, live poll
   * tally + a working `onVote`) so thread media/polls are fully
   * functional rather than display-only. When it returns `undefined` the
   * panel falls back to the context-free `deriveVariantFromBody`.
   */
  readonly buildVariant?:
    | ((reply: ThreadReply) => ChatMessageVariant | undefined)
    | undefined;
  /**
   * Live, folded game session for a reply that hosts a game tile —
   * resolved by the host from `useGameSessions` by the variant's
   * `sessionId`. When present (alongside a `game` variant from
   * `buildVariant`) the thread renders the SAME interactive tile the
   * main chat does, rather than a frozen create-phase card.
   */
  readonly gameSessionForReply?:
    | ((reply: ThreadReply) => GameSession | undefined)
    | undefined;
  /**
   * Emit a game move from a thread tile. Mirrors the main chat's
   * `onGameMove` — the host sends `com.ping.game_move` into the
   * conversation with the thread root as `parent_id`.
   */
  readonly onGameMove?:
    | ((sessionId: string, moveIdx: number, payload: string) => void)
    | undefined;
  /** Display name of the game opponent (the non-self player). */
  readonly gameOpponentName?: string | undefined;
  /**
   * Resolve a reader's display name for the "Read by" popover on a self
   * reply (parity with the main chat's `MessageRow.resolveReaderName`).
   * Supplied by the host only for GROUP threads; a 1:1 tick is
   * non-interactive.
   */
  readonly resolveReaderName?: ((userId: string) => string | undefined) | undefined;
  /** Resolve a reader's avatar for the "Read by" popover. */
  readonly resolveReaderAvatar?: ((userId: string) => string | undefined) | undefined;
  /**
   * Non-self member count in a GROUP thread. A self reply only flips to
   * the double-check (read) glyph once `readBy.length >= this`, matching
   * the main chat's group read semantics. Omit for 1:1 threads.
   */
  readonly groupNonSelfMemberCount?: number | undefined;
  /**
   * Begin EDITING a self reply — the host sets its composer into edit
   * mode (via `composerProps.editingMessage` + `onSubmitEdit`). Wired to
   * the tile's hover pencil; only offered on the local user's own
   * replies (parity with the main chat's `onEditPressFromHover`).
   */
  readonly onEditReply?: ((reply: ThreadReply) => void) | undefined;
  /**
   * Retry a FAILED reply send (parity with the main chat's bubble retry).
   * Preferred over the panel-level `onRetryReply` when both are present.
   */
  readonly onRetryReply?: ((reply: ThreadReply) => void) | undefined;
  /**
   * Tap a reply author's AVATAR → open their profile popover, anchored to
   * the tapped avatar (parity with the main chat's `onAvatarPress`). The
   * host receives the reply author's account id + the avatar's anchor ref
   * and calls `openMiniProfile(accountId, ref)`.
   */
  readonly onAvatarPress?:
    | ((userId: string, anchorRef: React.RefObject<unknown>) => void)
    | undefined;
  /**
   * Tap an @mention pill inside a reply body → open that user's profile
   * (parity with the main chat's `onMentionPress`, which opens the full
   * profile modal). The host receives the mention's user id.
   */
  readonly onMentionPress?: ((userId: string) => void) | undefined;
}

/**
 * The view-model contract we depend on. We accept the structural shape
 * rather than `ThreadViewModel` itself so the host can pass a wrapped
 * VM (e.g. one bound to a session store) without an import cycle.
 */
export interface ThreadOpenPanelProps {
  readonly vm: ThreadViewModelLike;
  /** True when the panel is visibly focused. Drives `markAsRead`. */
  readonly focused?: boolean;
  readonly onRetryReply?: ((reply: ThreadReply) => void) | undefined;
  readonly composerProps?: ThreadComposerProps | undefined;
  readonly rowActions?: ThreadRowActions | undefined;
  /**
   * Staged attachments for the thread composer's tray (host-owned, same
   * shape as the main chat). When present the tray renders above the
   * composer and `onFlushStagedAttachments` fires on send.
   */
  readonly stagedAttachments?: readonly StagedAttachment[] | undefined;
  readonly onRemoveStagedAttachment?: ((id: string) => void) | undefined;
  /** Sends + clears the staged attachments. Invoked on composer submit. */
  readonly onFlushStagedAttachments?: (() => Promise<void> | void) | undefined;
  readonly testID?: string;
}

// Distance (px) from the bottom within which an INCOMING reply still
// auto-scrolls the list (mirrors ChatRoom's sticky-bottom threshold).
const SCROLL_NEAR_BOTTOM_THRESHOLD = 240;

/** Map the richer thread reply status onto `Message.deliveryState`. */
function mapReplyStatusToDelivery(
  status: ThreadReplyStatus,
): 'pending' | 'sent' | 'failed' {
  if (status === 'failed') return 'failed';
  if (status === 'sending') return 'pending';
  return 'sent';
}

/**
 * True for a game state-update reply (`com.ping.game_move` / `_outcome` /
 * `_join` / `_reveal`). These fold into the live game tile hosted by the
 * `com.ping.game_create` reply — they must NOT render their own row (parity
 * with the main chat, which drops them from the timeline). Without this every
 * move/outcome renders a duplicate, wrong-kind (coinflip-default) game tile.
 */
function isFoldedThreadGameUpdate(rawBody: string | undefined): boolean {
  if (rawBody === undefined || rawBody.length === 0 || rawBody.charAt(0) !== '{') return false;
  try {
    const kind = (JSON.parse(rawBody) as { kind?: unknown }).kind;
    return (
      kind === 'com.ping.game_move' ||
      kind === 'com.ping.game_outcome' ||
      kind === 'com.ping.game_join' ||
      kind === 'com.ping.game_reveal'
    );
  } catch {
    return false;
  }
}

/**
 * Right-sidebar panel that renders the open thread. Closed-state and
 * any-state-but-open render is the host's responsibility — when
 * `vm.state.kind === 'closed'`, this panel renders `null` so it can be
 * unmounted from the layout without conditional rendering at the call
 * site.
 *
 * Auto-scroll: when a new reply arrives and the user is within the
 * sticky-bottom threshold (the FlatList scroll is at offset 0 because
 * `inverted` is false and we anchor by passing the reversed list — see
 * the `replies` derivation below), we scroll to top. The detection is
 * coarse on purpose; the precise "new replies" affordance is a follow-up.
 */
export const ThreadOpenPanel: React.FC<ThreadOpenPanelProps> = ({
  vm,
  focused = true,
  onRetryReply,
  composerProps,
  rowActions,
  stagedAttachments,
  onRemoveStagedAttachment,
  onFlushStagedAttachments,
  testID,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const theme = createTheme(colorScheme);
  // Thread mirrors the main chat's display density (bubble vs compact),
  // so a compact main chat gets compact thread rows too.
  const messageLayout = useUiPreferencesStore((s) => s.messageLayout);
  const listRef = useRef<FlatList<ThreadReply>>(null);
  const previousReplyCountRef = useRef(0);
  // Tracks whether the user is anchored near the bottom of the reply
  // list. Mirrors the main chat (ChatRoom.tsx): an INCOMING reply only
  // auto-scrolls when the user is already near the bottom, but a SENT
  // reply (local echo) always scrolls — `forceScrollNextGrowthRef`
  // forces the next growth regardless of scroll position.
  const nearBottomRef = useRef(true);
  const forceScrollNextGrowthRef = useRef(false);
  // Last observed FlatList content height. `handleContentSizeChange` scrolls
  // ONLY when the content actually GREW (a real new/expanding row in THIS
  // thread) — not on every re-render. react-native-web re-fires
  // `onContentSizeChange` on any re-render (e.g. when an unrelated main-chat
  // message churns the shared conversation subscription and pushes a new — but
  // equivalent — replies array), which previously yanked the thread to the
  // bottom on every main-chat send. The height guard scopes the scroll to this
  // surface's own growth.
  const lastContentHeightRef = useRef(0);

  const state = vm.state;

  useEffect(() => {
    if (focused && (state.kind === 'open' || state.kind === 'loading' || state.kind === 'error')) {
      vm.markAsRead();
    }
  }, [focused, state, vm]);

  const stateReplies: readonly ThreadReply[] = state.kind === 'open' ? state.replies : [];
  // Drop game state-update replies (move/outcome/join/reveal) — they fold into
  // the single game_create tile, so rendering them would show duplicate,
  // wrong-kind tiles. Everything downstream (list, grouping, scroll count)
  // consumes this filtered array so the two stay consistent.
  const replies = useMemo<readonly ThreadReply[]>(
    () => stateReplies.filter((r) => !isFoldedThreadGameUpdate(r.rawBody)),
    [stateReplies],
  );
  const replyCount = replies.length;

  // Reset anchoring + force an initial scroll-to-end when the panel
  // (re)opens, so a freshly-opened thread lands on its latest reply.
  useEffect(() => {
    if (state.kind === 'open') {
      nearBottomRef.current = true;
      forceScrollNextGrowthRef.current = true;
      previousReplyCountRef.current = 0;
      lastContentHeightRef.current = 0;
    } else {
      previousReplyCountRef.current = 0;
      lastContentHeightRef.current = 0;
    }
    // Re-key on the thread root so switching threads re-anchors.
  }, [state.kind, state.kind === 'open' ? state.parent.messageId : undefined]);

  useEffect(() => {
    if (state.kind !== 'open') {
      previousReplyCountRef.current = 0;
      return;
    }
    if (replyCount > previousReplyCountRef.current) {
      // Newest reply rendered last in the data array. Scroll to the
      // bottom when EITHER the user is already near the bottom (an
      // incoming reply shouldn't yank a user who's reading history) OR
      // the growth was triggered by the local user's own send (the
      // force flag), mirroring the main chat's send-always-scrolls
      // behaviour.
      if (nearBottomRef.current || forceScrollNextGrowthRef.current) {
        forceScrollNextGrowthRef.current = false;
        // Defer so FlatList has measured the new row before scrolling.
        requestAnimationFrame(() => {
          listRef.current?.scrollToEnd({ animated: true });
        });
      }
    }
    previousReplyCountRef.current = replyCount;
  }, [state.kind, replyCount]);

  // Track whether the user is anchored near the bottom of the reply
  // list (non-inverted FlatList: "near bottom" = within a threshold of
  // the max scroll offset). Drives the gated auto-scroll above.
  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>): void => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const distanceFromBottom =
        contentSize.height - (contentOffset.y + layoutMeasurement.height);
      nearBottomRef.current = distanceFromBottom <= SCROLL_NEAR_BOTTOM_THRESHOLD;
    },
    [],
  );

  // When the list's content size GROWS while the user is anchored at the
  // bottom, keep them pinned. Belt-and-suspenders alongside the length-change
  // effect: covers growth that doesn't change the reply COUNT (e.g. an image
  // reply finishing layout / a tile expanding). Critically, we scroll ONLY on
  // an actual height increase (or a forced local send) — react-native-web
  // re-fires this on every re-render even when the content is unchanged, so a
  // plain `nearBottom` gate scrolled the thread on unrelated main-chat sends
  // (the shared conversation subscription pushes a fresh-but-equivalent replies
  // array). Comparing the reported height scopes the scroll to this thread's
  // own growth.
  const handleContentSizeChange = useCallback(
    (_width: number, height: number): void => {
      if (state.kind !== 'open') return;
      const grew = height > lastContentHeightRef.current + 1;
      lastContentHeightRef.current = height;
      const forced = forceScrollNextGrowthRef.current;
      if (!grew && !forced) return;
      if (nearBottomRef.current || forced) {
        forceScrollNextGrowthRef.current = false;
        listRef.current?.scrollToEnd({ animated: true });
      }
    },
    [state.kind],
  );

  const keyExtractor = useCallback((reply: ThreadReply) => reply.id, []);

  // Render each thread reply through the SAME `MessageRow` the main chat
  // uses — so threads get identical tiles, hover actions, reactions, and
  // the speech tail. `inThread` hides "Reply in Thread" (no nesting).
  const renderItem = useCallback<ListRenderItem<ThreadReply>>(
    ({ item: reply, index }) => {
      const previous = replies[index - 1];
      const next = replies[index + 1];
      const grouped = previous !== undefined && previous.authorId === reply.authorId;
      const isLastOfGroup = next === undefined || next.authorId !== reply.authorId;
      const isStacked = messageLayout === 'stacked';

      // Text replies render the FULL body — rich Slack-mrkdwn (bold / italic /
      // code / links) + tappable @mention pills — not the truncated one-line
      // preview. `MessageBubble` decodes the envelope (or a legacy plain
      // string) from `message.body` internally, exactly like the main chat, so
      // handing it `rawBody` is all that's needed. Media / other kinds keep
      // their short preview since their tile renders from the `variant`.
      const parsedReply =
        reply.rawBody !== undefined && reply.rawBody.length > 0
          ? parseEnvelope(reply.rawBody)
          : undefined;
      const displayBody =
        parsedReply?.kind === 'com.ping.text' ? (reply.rawBody as string) : reply.body;

      const message: Message = {
        id: reply.id,
        conversationId: reply.threadId,
        senderId: reply.authorId,
        body: displayBody,
        hlc: '',
        contentHash: '',
        timestamp: reply.timestamp,
        isSelf: reply.isSelf,
        deliveryState: mapReplyStatusToDelivery(reply.status),
        // Read receipts (parity with the main chat): a self reply flips
        // to the double-check glyph once `readBy` is non-empty (1:1) or
        // reaches the group threshold. Projected by the host from the row.
        ...(reply.readBy !== undefined && reply.readBy.length > 0
          ? { readBy: reply.readBy.map((r) => ({ userId: r.userId, timestamp: r.timestamp })) }
          : {}),
        ...(reply.editedAt !== undefined ? { editedAt: reply.editedAt } : {}),
      };
      const variant =
        rowActions?.buildVariant?.(reply) ??
        (reply.rawBody !== undefined && reply.rawBody.length > 0
          ? deriveVariantFromBody(reply.rawBody, { message })
          : undefined);
      const reactions = rowActions?.reactionsByReplyId?.[reply.id];
      // Resolve the live game session for a `game` variant so the tile
      // is interactive (current phase + folded moves) instead of a
      // frozen create-phase card — parity with the main chat's
      // ChatRoom.renderItem game wiring.
      const gameSession =
        variant?.kind === 'game'
          ? rowActions?.gameSessionForReply?.(reply)
          : undefined;

      return (
        <MessageRow
          message={message}
          inThread
          layoutMode={isStacked ? 'stacked' : 'bubble'}
          isLastOfGroup={isLastOfGroup}
          isFirstOfGroup={!grouped}
          {...(isStacked ? { showHeader: !grouped } : {})}
          {...(variant !== undefined ? { variant } : {})}
          {...(gameSession !== undefined ? { gameSession } : {})}
          {...(variant?.kind === 'game' && rowActions?.onGameMove !== undefined
            ? { onGameMove: rowActions.onGameMove }
            : {})}
          {...(variant?.kind === 'game' && rowActions?.gameOpponentName !== undefined
            ? { gameOpponentName: rowActions.gameOpponentName }
            : {})}
          {...((isStacked ? !grouped : !reply.isSelf && !grouped)
            ? { senderName: reply.isSelf ? 'You' : reply.authorName }
            : {})}
          {...((isStacked ? !grouped : !reply.isSelf && !grouped)
            ? {
                senderAvatar: {
                  initials: reply.isSelf ? 'You' : reply.authorName,
                  ...(reply.authorImageUri !== null
                    ? { imageUri: reply.authorImageUri }
                    : {}),
                },
              }
            : {})}
          {...(!isStacked && !reply.isSelf && grouped ? { groupAvatarGutter: true } : {})}
          {...(reactions !== undefined ? { reactions } : {})}
          {...(rowActions?.onReact !== undefined
            ? { onReactPress: () => rowActions.onReact?.(reply) }
            : {})}
          {...(rowActions?.onToggleReaction !== undefined
            ? { onToggleReaction: (emoji: string) => rowActions.onToggleReaction?.(reply, emoji) }
            : {})}
          {...(rowActions?.onMenuAction !== undefined
            ? { onMenuAction: (action: MessageMenuAction) => rowActions.onMenuAction?.(action, reply) }
            : {})}
          {...(rowActions?.isReplyPinned?.(reply) === true ? { isPinned: true } : {})}
          {...(rowActions?.isReplySaved?.(reply) === true ? { isSaved: true } : {})}
          {...(rowActions?.isReplyStarred?.(reply) === true ? { isStarred: true } : {})}
          {...(reply.isSelf && rowActions?.onEditReply !== undefined
            ? { onEditPressFromHover: () => rowActions.onEditReply?.(reply) }
            : {})}
          {...(rowActions?.resolveReaderName !== undefined
            ? { resolveReaderName: rowActions.resolveReaderName }
            : {})}
          {...(rowActions?.resolveReaderAvatar !== undefined
            ? { resolveReaderAvatar: rowActions.resolveReaderAvatar }
            : {})}
          {...(rowActions?.groupNonSelfMemberCount !== undefined
            ? { groupNonSelfMemberCount: rowActions.groupNonSelfMemberCount }
            : {})}
          {...(rowActions?.onAvatarPress !== undefined
            ? { onAvatarPress: rowActions.onAvatarPress }
            : {})}
          {...(rowActions?.onMentionPress !== undefined
            ? { onMentionPress: rowActions.onMentionPress }
            : {})}
          {...(rowActions?.onRetryReply !== undefined
            ? { onRetryPress: () => rowActions.onRetryReply?.(reply) }
            : onRetryReply !== undefined
              ? { onRetryPress: () => onRetryReply(reply) }
              : {})}
        />
      );
    },
    [onRetryReply, replies, rowActions, messageLayout],
  );

  // Thread replies use the SAME rich composer as the main chat (Enter to
  // send, formatting, emoji, mentions). `inReplyTo` from the composer is
  // ignored — the thread identifier already scopes the reply.
  const handleSend = useCallback(
    async (body: string): Promise<void> => {
      // Sending a reply ALWAYS scrolls to the bottom — even when the
      // user had scrolled up to read history — matching the main chat.
      // The local echo grows the reply list, and this flag makes the
      // length-change effect scroll regardless of `nearBottomRef`.
      forceScrollNextGrowthRef.current = true;
      const trimmed = body.trim();
      if (trimmed.length > 0) {
        await vm.sendReply(trimmed);
      }
      // Flush any staged attachments (sends them into the thread + clears
      // the tray). Lets the composer Send button submit text + media in
      // one go, exactly like the main chat composite send.
      await onFlushStagedAttachments?.();
    },
    [vm, onFlushStagedAttachments],
  );

  const closeContent = useMemo<React.ReactNode>(() => null, []);

  if (state.kind === 'closed') {
    return closeContent;
  }

  const body = renderBody({
    state,
    theme,
    listRef,
    replies,
    keyExtractor,
    renderItem,
    onRetry: vm.retry,
    onScroll: handleScroll,
    onContentSizeChange: handleContentSizeChange,
  });

  const isOpenLike = state.kind === 'open';
  const composerDisabled = state.kind !== 'open' || (state.kind === 'open' && state.parent.deleted);

  const placeholderHeader = (
    <View className="flex-row items-center justify-between border-b border-light-border-secondary px-4 py-3 dark:border-[#222429]">
      <Text className="font-sans text-[20px] font-bold leading-[26px] text-light-text-primary dark:text-[#F5F6F8]">
        {threadsStrings.ping_threads_panel_title}
      </Text>
      <Pressable
        onPress={vm.close}
        accessibilityRole="button"
        accessibilityLabel={threadsStrings.ping_threads_panel_close}
        className="h-8 w-8 items-center justify-center rounded-full bg-light-surface-secondary active:opacity-70 dark:bg-[#222429]"
        hitSlop={8}
      >
        <X
          size={18}
          strokeWidth={2}
          color={scheme === 'dark' ? '#B5BAC3' : theme.colors.text.secondary}
        />
      </Pressable>
    </View>
  );

  return (
    // Cap every message tile in the thread side panel to a fixed width — the
    // panel is far narrower than the main chat, so tiles (self/other, bubble/
    // compact) read this and shrink to fit. `null` elsewhere = no cap.
    <TileMaxWidthContext.Provider value={THREAD_TILE_MAX_WIDTH}>
    <View
      className="h-full w-full flex-1 bg-light-surface-primary dark:bg-[#11131A]"
      testID={testID}
    >
      {isOpenLike && (
        <ThreadHeader parent={state.parent} replyCount={replies.length} onClose={vm.close} />
      )}
      {state.kind === 'loading' && placeholderHeader}
      {state.kind === 'error' && placeholderHeader}
      {isOpenLike && state.participants.length > 0 && (
        <ThreadParticipantStrip participants={state.participants} />
      )}
      <View className="min-h-0 flex-1 pr-1">{body}</View>
      {stagedAttachments !== undefined && stagedAttachments.length > 0 ? (
        <AttachmentTray
          attachments={stagedAttachments}
          onRemove={onRemoveStagedAttachment ?? ((): void => undefined)}
        />
      ) : null}
      <ChatRoomComposerRich
        {...(composerProps ?? {})}
        onSend={handleSend}
        disabled={composerDisabled}
        // Thread + call-thread panels are narrow — tighten the row inset.
        denseComposerPadding
        containerContentClassName="px-sm pt-sm pb-lg"
        hasStagedAttachments={
          stagedAttachments !== undefined && stagedAttachments.length > 0
        }
      />
    </View>
    </TileMaxWidthContext.Provider>
  );
};

interface RenderBodyArgs {
  readonly state: ThreadState;
  readonly theme: ReturnType<typeof createTheme>;
  readonly listRef: React.RefObject<FlatList<ThreadReply>>;
  readonly replies: readonly ThreadReply[];
  readonly keyExtractor: (reply: ThreadReply) => string;
  readonly renderItem: ListRenderItem<ThreadReply>;
  readonly onRetry: () => void;
  readonly onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
  readonly onContentSizeChange: (width: number, height: number) => void;
}

function renderBody(args: RenderBodyArgs): React.ReactElement {
  const {
    state,
    theme,
    listRef,
    replies,
    keyExtractor,
    renderItem,
    onRetry,
    onScroll,
    onContentSizeChange,
  } = args;

  if (state.kind === 'loading') {
    return (
      <View style={styles.center}>
        <ActivityIndicator
          size="small"
          color={theme.colors.accent.indigo}
          accessibilityLabel={threadsStrings.ping_threads_loading_label}
        />
      </View>
    );
  }
  if (state.kind === 'error') {
    return (
      <View style={styles.center}>
        <Text style={[styles.errorTitle, { color: theme.colors.text.primary }]}>
          {threadsStrings.ping_threads_error_title}
        </Text>
        <Pressable
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={threadsStrings.ping_threads_error_retry}
          style={({ pressed }) => [
            styles.retryButton,
            { backgroundColor: theme.colors.accent.indigo },
            pressed && styles.pressed,
          ]}
        >
          <Text style={styles.retryText}>{threadsStrings.ping_threads_error_retry}</Text>
        </Pressable>
      </View>
    );
  }
  if (state.kind === 'open') {
    // `replies` here is the RENDERABLE (game-update-filtered) set — a thread
    // holding only folded game moves would otherwise render an empty list.
    if (replies.length === 0) {
      return <ThreadEmptyState />;
    }
    return (
      <FlatList<ThreadReply>
        ref={listRef}
        data={replies}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        initialNumToRender={20}
        maxToRenderPerBatch={10}
        windowSize={10}
        removeClippedSubviews={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        onContentSizeChange={onContentSizeChange}
        contentContainerStyle={styles.listContent}
      />
    );
  }
  return <View />;
}

const styles = StyleSheet.create({
  pressed: {
    opacity: 0.7,
  } satisfies ViewStyle,
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
  } satisfies TextStyle,
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  } satisfies ViewStyle,
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  } satisfies TextStyle,
  listContent: {
    paddingVertical: 8,
  } satisfies ViewStyle,
});
